import React, { useEffect, useState } from "react";
import {
  Button,
  message,
  Modal,
  Row,
  Col,
  Divider,
  Switch,
  Spin,
  Form,
  Input,
} from "antd";
import {
  MobileOutlined,
  DesktopOutlined,
  LockOutlined,
  CheckOutlined,
  DownOutlined,
  RightOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { RESTExecutor } from "../../lib/domain";
import moment from "moment";
import { RoundedIconButton } from "../../components/forms/buttons";
import { Transition, animated } from "react-spring/renderprops";
import "./manageSessions.css";
import CustomSpin from "../../components/custom_spin";

const ManageSessionsPage = ({
  domains,
  userInfo,
  refreshUserData,
  ...props
}) => {
  const [sessions, setSessions] = useState({ trusted: [], untrusted: [] });
  const [loading, setLoading] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [hoveredButton, setHoveredButton] = useState(null);
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pendingAction, setPendingAction] = useState("");
  const [password, setPassword] = useState("");
  const [passwordForm] = Form.useForm();
  const [untrustedExpanded, setUntrustedExpanded] = useState(false);

  const validateCurrentPasswordInput = (password) => {
    if (!password) return false;
    return password.trim().length > 0;
  };

  const getPasswordIndicator = (password) => {
    if (!password) return null;
    const isValid = validateCurrentPasswordInput(password);
    return (
      <div className={`password-indicator ${isValid ? "valid" : "invalid"}`}>
        {isValid ? <CheckOutlined style={{ fontSize: "8px" }} /> : ""}
      </div>
    );
  };

  let list_trusted_devices = domains?.trustedDevices
    ? RESTExecutor.list()
        .config({
          label: "list",
        })
        .callbacks(
          (success) => {
            setLoading(false);

            // Handle both old (array) and new ({ trusted, untrusted }) API structures
            const raw = success.result;
            let trustedDevices = [];
            let untrustedDevices = [];

            if (Array.isArray(raw)) {
              // Backward compatibility: previous API returned a flat array
              trustedDevices = raw || [];
            } else {
              const result = raw || {};
              trustedDevices = result.trusted || [];
              untrustedDevices = result.untrusted || [];
            }

            setSessions({
              trusted: trustedDevices,
              untrusted: untrustedDevices,
            });

            // Find current session from trusted devices first, then untrusted
            const current =
              trustedDevices.find((session) => session.is_current) ||
              untrustedDevices.find((session) => session.is_current) ||
              trustedDevices[0] ||
              untrustedDevices[0];
            setCurrentSessionId(current?.id);
          },
          (error) => {
            setLoading(false);

            message.error(error.error_message || "Failed to load devices");
          }
        )
        .connect(domains.trustedDevices)
    : null;

  let remove_trusted_device = domains?.trustedDevices
    ? RESTExecutor.delete()
        .config({
          label: "detail",
        })
        .callbacks(
          (success) => {
            message.success("Session removed successfully");
            list_trusted_devices?.execute(true);
          },
          (error) => {
            message.error(error.error_message || "Failed to remove session");
            setLoading(false);
          }
        )
        .connect(domains.trustedDevices)
    : null;

  let remove_all_trusted_devices = domains?.trustedDevices
    ? RESTExecutor.delete()
        .config({
          label: "remove_all",
        })
        .callbacks(
          (success) => {
            message.success("All sessions removed successfully");
            list_trusted_devices?.execute(true);
          },
          (error) => {
            message.error(
              error.error_message || "Failed to remove all sessions"
            );
          }
        )
        .connect(domains.trustedDevices)
    : null;

  // New unified 2FA toggle function that uses enable_2fa endpoint with enable parameter and password
  let toggle_2fa = domains?.user
    ? RESTExecutor.post()
        .config({
          label: "enable_2fa",
        })
        .callbacks(
          (success) => {
            // The success callback will be set dynamically based on enable/disable action
          },
          (error) => {
            message.error(error.error_message || `Failed to toggle 2FA`);
            setTwoFALoading(false);
          }
        )
        .connect(domains.user)
    : null;

  useEffect(() => {
    if (domains?.trustedDevices && list_trusted_devices) {
      setLoading(true);
      list_trusted_devices.execute(true);
    } else {
      setSessions({ trusted: [], untrusted: [] });
      setLoading(false);
    }
  }, [domains]);

  const handleRefreshTrusted = () => {
    if (domains?.trustedDevices && list_trusted_devices) {
      setLoading(true);
      list_trusted_devices.execute(true);
    }
  };

  const handleRefreshUntrusted = () => {
    if (domains?.trustedDevices && list_trusted_devices) {
      setLoading(true);
      list_trusted_devices.execute(true);
    }
  };

  useEffect(() => {
    if (userInfo) {
      setTwoFAEnabled(userInfo.two_factor_auth_enabled || false);
      setTwoFALoading(false);
    }
  }, [userInfo]);

  const handleRemoveSession = (sessionId) => {
    Modal.confirm({
      title: "Remove Device",
      content: "Are you sure you want to remove this device ?",
      okText: "Remove",
      okType: "danger",
      cancelText: "Cancel",
      onOk() {
        if (remove_trusted_device) {
          setLoading(true);
          remove_trusted_device.forId(sessionId).execute();
        } else {
          message.error("Domain not available");
        }
      },
    });
  };

  const handleRemoveAllSessions = () => {
    Modal.confirm({
      title: "Remove All Devices",
      content: "Are you sure you want to remove all devices?",
      okText: "Remove",
      okType: "danger",
      cancelText: "Cancel",
      onOk() {
        if (remove_all_trusted_devices) {
          remove_all_trusted_devices.execute();
          setLoading(true);
        } else {
          message.error("Domain not available");
        }
      },
    });
  };

  const handleToggle2FA = () => {
    const action = twoFAEnabled ? "disable" : "enable";
    setPendingAction(action);
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = (values) => {
    if (!toggle_2fa) {
      message.error("2FA service not available");
      return;
    }

    if (!validateCurrentPasswordInput(values.current_password)) {
      message.error("Please enter your current password");
      return;
    }

    const isEnabling = pendingAction === "enable";
    setTwoFALoading(true);

    // Set up dynamic callback based on the action
    toggle_2fa.callbacks(
      (success) => {
        setTwoFAEnabled(isEnabling);
        setTwoFALoading(false);
        message.success(
          `2FA ${isEnabling ? "enabled" : "disabled"} successfully`
        );
        if (refreshUserData) {
          refreshUserData();
        }
        setShowPasswordModal(false);
        setPassword("");
        passwordForm.resetFields();
      },
      (error) => {
        message.error(error.error_message || `Failed to ${pendingAction} 2FA`);
        setTwoFALoading(false);
      }
    );

    // Execute with enable parameter and password
    toggle_2fa.execute({
      enable: isEnabling,
      password: values.current_password,
    });
  };

  const handlePasswordModalCancel = () => {
    setShowPasswordModal(false);
    setPassword("");
    setPendingAction("");
    passwordForm.resetFields();
  };

  const formatLastAccessed = (dateString) => {
    return moment(dateString).format("MMMM D, YYYY [at] HH:mm");
  };

  const getDeviceIcon = (session) => {
    if (session.device_type) {
      return session.device_type === "mobile" ? (
        <MobileOutlined style={{ fontSize: "18px" }} />
      ) : (
        <DesktopOutlined style={{ fontSize: "18px" }} />
      );
    }

    const deviceName = session.device_name?.toLowerCase() || "";
    const isMobile =
      deviceName.includes("mobile") ||
      deviceName.includes("iphone") ||
      deviceName.includes("android") ||
      deviceName.includes("ios") ||
      deviceName.includes("tablet") ||
      deviceName.includes("ipad");

    return isMobile ? (
      <MobileOutlined style={{ fontSize: "18px" }} />
    ) : (
      <DesktopOutlined style={{ fontSize: "18px" }} />
    );
  };

  const getSessionFingerprint = (session) => {
    return session?.device_finger_print || null;
  };

  const handleRemoveSessionClick = (session) => {
    const fp = getSessionFingerprint(session);
    if (!fp) {
      message.error("Device fingerprint not available for this session");
      return;
    }
    handleRemoveSession(fp);
  };

  const renderSessionList = (sessionsList, isUntrusted = false) => {
    if (sessionsList.length === 0) {
      return (
        <div style={{ textAlign: "center", padding: "40px", color: "#91979F" }}>
          <p>No {isUntrusted ? "untrusted" : "trusted"} sessions found.</p>
        </div>
      );
    }

    return sessionsList.map((session, index) => (
      <div key={session.id}>
        <Row
          align="middle"
          style={{
            padding: "16px 0",
            minHeight: "60px",
          }}
        >
          <Col span={2}>
            <div
              style={{
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#F5F5F5",
                borderRadius: "8px",
                color: "#91979F",
              }}
            >
              {getDeviceIcon(session)}
            </div>
          </Col>

          <Col span={16} style={{ paddingLeft: "16px" }}>
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "4px",
                }}
              >
                <span
                  style={{
                    fontFamily: "Inter",
                    fontSize: "16px",
                    fontWeight: "500",
                    color: "#343C46",
                  }}
                >
                  {session.device_name}
                </span>
                {session.is_current && (
                  <span
                    style={{
                      backgroundColor: "#E8F4FD",
                      color: "#1890FF",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize: "10px",
                      fontWeight: "600",
                      fontFamily: "Inter",
                    }}
                  >
                    Current
                  </span>
                )}
                {isUntrusted && (
                  <span
                    style={{
                      backgroundColor: "#FFF2E8",
                      color: "#FA8C16",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize: "10px",
                      fontWeight: "600",
                      fontFamily: "Inter",
                    }}
                  >
                    Untrusted
                  </span>
                )}
              </div>
              <div
                style={{
                  fontFamily: "Inter",
                  fontSize: "14px",
                  fontWeight: "400",
                  color: "#91979F",
                }}
              >
                {session.ip_address} | {formatLastAccessed(session.created_at)}
              </div>
            </div>
          </Col>

          <Col span={6} style={{ textAlign: "right" }}>
            <Button
              type="text"
              danger
              onClick={() => handleRemoveSessionClick(session)}
              onMouseEnter={() => setHoveredButton(session.id)}
              onMouseLeave={() => setHoveredButton(null)}
              style={{
                fontFamily: "Inter",
                fontSize: "14px",
                fontWeight: "600",
                padding: "4px 16px",
                backgroundColor:
                  hoveredButton === session.id ? "#E82E49" : "transparent",
                color: hoveredButton === session.id ? "#FFFFFF" : "#E82E49",
                borderRadius: "4px",
                transition: "all 0.3s ease",
              }}
            >
              Remove
            </Button>
          </Col>
        </Row>

        {index < sessionsList.length - 1 && <Divider style={{ margin: "0" }} />}
      </div>
    ));
  };

  const totalSessions = sessions.trusted.length + sessions.untrusted.length;

  return (
    <>
      <div className="edit-form-container">
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              margin: 0,
            }}
          >
            <div>
              <p className="edit-form-header" style={{ marginBottom: 4 }}>
                Manage Sessions
              </p>
              <p
                style={{
                  margin: 0,
                  color: "#91979F",
                  fontSize: "14px",
                  fontFamily: "Inter",
                  fontWeight: "400",
                }}
              >
                You have{" "}
                <strong style={{ color: "#343C46" }}>
                  {totalSessions} active sessions
                </strong>
                .
              </p>
            </div>
            <div style={{ textAlign: "right" }}>
              <Button
                danger
                disabled={totalSessions === 0}
                onClick={handleRemoveAllSessions}
                onMouseEnter={() => setHoveredButton("removeAll")}
                onMouseLeave={() => setHoveredButton(null)}
                style={{
                  fontFamily: "Inter",
                  fontWeight: "600",
                  fontSize: "14px",
                  backgroundColor:
                    hoveredButton === "removeAll" ? "#E82E49" : undefined,
                  color: hoveredButton === "removeAll" ? "#FFFFFF" : undefined,
                  borderColor:
                    hoveredButton === "removeAll" ? "#E82E49" : undefined,
                  transition: "all 0.3s ease",
                }}
              >
                Remove All Devices
              </Button>
            </div>
          </div>

          <Divider style={{ margin: "16px 0" }} />

          {/* Trusted Devices Section */}
          <div style={{ marginBottom: "16px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <p
                style={{
                  margin: 0,
                  color: "#343C46",
                  fontSize: "16px",
                  fontFamily: "Inter",
                  fontWeight: "600",
                }}
              >
                Trusted Devices ({sessions.trusted.length})
              </p>
              <Button
                type="text"
                onClick={handleRefreshTrusted}
                title="Refresh trusted devices"
                icon={<ReloadOutlined />}
              />
            </div>
            <p
              style={{
                margin: "4px 0 0 0",
                color: "#91979F",
                fontSize: "14px",
                fontFamily: "Inter",
                fontWeight: "400",
              }}
            >
              These are your trusted devices that you've previously authorized.
            </p>
          </div>
          <CustomSpin
            data={sessions.trusted}
            isLoading={loading}
            noDataMessage={"No Device Available"}
          >
            {renderSessionList(sessions.trusted)}
          </CustomSpin>

          {/* Untrusted Devices Section */}
          {sessions.untrusted.length > 0 && (
            <>
              {sessions.trusted.length > 0 && (
                <Divider style={{ margin: "24px 0" }} />
              )}

              <div className="expandable-card">
                <div
                  className="header"
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "nowrap",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => setUntrustedExpanded(!untrustedExpanded)}
                >
                  <div>
                    <div
                      style={{
                        margin: 0,
                        color: "#343C46",
                        fontSize: "16px",
                        fontFamily: "Inter",
                        fontWeight: "600",
                      }}
                    >
                      Untrusted Devices ({sessions.untrusted.length})
                    </div>
                    <div
                      style={{
                        margin: "4px 0 0 0",
                        color: "#91979F",
                        fontSize: "14px",
                        fontFamily: "Inter",
                        fontWeight: "400",
                      }}
                    >
                      These devices may require additional verification.
                    </div>
                  </div>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      type="text"
                      onClick={handleRefreshUntrusted}
                      title="Refresh untrusted devices"
                      icon={<ReloadOutlined />}
                    />
                    <RoundedIconButton
                      direction={untrustedExpanded ? "right" : "down"}
                      onClick={() => {
                        setUntrustedExpanded(!untrustedExpanded);
                      }}
                    />
                  </div>
                </div>

                <Transition
                  native
                  items={untrustedExpanded}
                  from={{ overflow: "hidden", height: 0 }}
                  enter={[{ height: "auto" }]}
                  leave={{ height: 0 }}
                >
                  {(show) =>
                    show &&
                    ((props) => (
                      <animated.div style={props}>
                        <div style={{ padding: "0 16px 16px 16px" }}>
                          <CustomSpin
                            data={sessions.untrusted}
                            noDataMessage={"No Device Available"}
                            isLoading={loading}
                          >
                            {renderSessionList(sessions.untrusted, true)}
                          </CustomSpin>
                        </div>
                      </animated.div>
                    ))
                  }
                </Transition>
              </div>
            </>
          )}
        </>
      </div>

      <div className="edit-form-container">
        <div style={{ marginBottom: "16px" }}>
          <p className="edit-form-header">Two Factor Authentication</p>
          <p
            style={{
              margin: 0,
              color: "#91979F",
              fontSize: "14px",
              fontFamily: "Inter",
              fontWeight: "400",
            }}
          >
            Enhance your account security by enabling 2FA.
          </p>
        </div>

        <Row
          align="middle"
          style={{
            padding: "16px 0",
            minHeight: "60px",
          }}
        >
          <Col span={18}>
            <div>
              <div
                style={{
                  fontFamily: "Inter",
                  fontSize: "16px",
                  fontWeight: "500",
                  color: "#343C46",
                  marginBottom: "4px",
                }}
              >
                Two-Factor Authentication
              </div>
              <div
                style={{
                  fontFamily: "Inter",
                  fontSize: "14px",
                  fontWeight: "400",
                  color: "#91979F",
                }}
              >
                {twoFAEnabled
                  ? "Your account is protected with 2FA"
                  : "Add an extra layer of security to your account"}
              </div>
            </div>
          </Col>

          <Col span={6} style={{ textAlign: "right" }}>
            {twoFALoading ? (
              <Spin size="small" />
            ) : (
              <Switch
                checked={twoFAEnabled}
                onChange={handleToggle2FA}
                loading={twoFALoading}
                style={{
                  backgroundColor: twoFAEnabled ? undefined : "#d9d9d9",
                }}
              />
            )}
          </Col>
        </Row>
      </div>

      <Modal
        open={showPasswordModal}
        onCancel={handlePasswordModalCancel}
        footer={null}
        width={544}
        centered
        bodyStyle={{
          padding: 0,
          background: "#FAFAFA",
          borderRadius: 16,
        }}
        style={{
          top: 0,
        }}
        maskStyle={{
          background: "rgba(0, 0, 0, 0.45)",
        }}
      >
        <div
          style={{
            width: "100%",
            paddingLeft: 32,
            paddingRight: 32,
            paddingTop: 40,
            paddingBottom: 40,
            background: "#FAFAFA",
            borderRadius: 16,
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            gap: 18,
            display: "flex",
          }}
        >
          <div
            style={{
              alignSelf: "stretch",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              gap: 24,
              display: "flex",
            }}
          >
            <div
              style={{
                flex: "1 1 0",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "flex-start",
                gap: 2,
                display: "flex",
              }}
            >
              <div
                style={{
                  alignSelf: "stretch",
                  color: "#343C46",
                  fontSize: 24,
                  fontFamily: "Inter",
                  fontWeight: "700",
                  wordWrap: "break-word",
                }}
              >
                {pendingAction === "enable" ? "Enable" : "Disable"} 2FA
              </div>
              <div
                style={{
                  color: "#636972",
                  fontSize: 12,
                  fontFamily: "Inter",
                  fontWeight: "400",
                  wordWrap: "break-word",
                  lineHeight: "16px",
                  marginBottom: "8px",
                }}
              >
                {pendingAction === "enable" ? (
                  <>
                    <div style={{ marginBottom: "6px" }}>
                      <span style={{ fontWeight: "600", color: "#343C46" }}>
                        Note:
                      </span>{" "}
                      OTP codes will be sent to your registered email
                      <span
                        style={{
                          fontWeight: "500",
                          color: "#143E9F",
                          backgroundColor: "#F0F4FF",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          marginLeft: "4px",
                        }}
                      >
                        {userInfo?.email || "your email"}
                      </span>
                    </div>
                    <div>
                      Please ensure you have access to this email before
                      enabling 2FA.
                    </div>
                  </>
                ) : (
                  <div>
                    Disabling 2FA will remove the extra security layer from your
                    account
                    <span
                      style={{
                        fontWeight: "500",
                        color: "#143E9F",
                        backgroundColor: "#F0F4FF",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        marginLeft: "4px",
                      }}
                    >
                      {userInfo?.email || "your email"}
                    </span>
                  </div>
                )}
              </div>
              <div
                style={{
                  color: "#636972",
                  fontSize: 12,
                  fontFamily: "Inter",
                  fontWeight: "400",
                  wordWrap: "break-word",
                  lineHeight: "16px",
                }}
              >
                Please enter your current password to confirm this change.
              </div>
            </div>
            <img
              src="images/tigg_logo.png"
              style={{
                width: "auto",
                height: "33.18px",
              }}
              alt="Tigg Logo"
            />
          </div>

          <div
            style={{
              alignSelf: "stretch",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              gap: 16,
              display: "flex",
            }}
          >
            <Form
              requiredMark={false}
              colon={false}
              form={passwordForm}
              name="password-confirmation"
              className="password-modal-form"
              onFinish={handlePasswordSubmit}
              layout="vertical"
              style={{ width: "100%" }}
            >
              <div
                style={{
                  alignSelf: "stretch",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  gap: 24,
                  display: "flex",
                }}
              >
                <div
                  style={{
                    alignSelf: "stretch",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    gap: 8,
                    display: "flex",
                  }}
                >
                  <div
                    style={{
                      alignSelf: "stretch",
                      flexDirection: "column",
                      justifyContent: "flex-start",
                      alignItems: "flex-start",
                      gap: 12,
                      display: "flex",
                    }}
                  >
                    <div
                      style={{
                        alignSelf: "stretch",
                        flexDirection: "column",
                        justifyContent: "flex-start",
                        alignItems: "flex-start",
                        gap: 4,
                        display: "flex",
                        paddingBottom: 8,
                      }}
                    >
                      <div
                        style={{
                          color: "#636972",
                          fontSize: 12,
                          fontFamily: "Inter",
                          fontWeight: "500",
                          wordWrap: "break-word",
                        }}
                      >
                        Current Password*
                      </div>

                      <Form.Item
                        name="current_password"
                        style={{ alignSelf: "stretch", width: "100%" }}
                        rules={[
                          {
                            required: true,
                            message: "Please enter your current password!",
                          },
                        ]}
                      >
                        <Input.Password
                          prefix={<LockOutlined />}
                          suffix={getPasswordIndicator(password)}
                          placeholder="Enter your current password"
                          onChange={(e) => setPassword(e.target.value)}
                        />
                      </Form.Item>
                    </div>
                  </div>
                </div>
              </div>

              <div
                style={{
                  alignSelf: "stretch",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  gap: 24,
                  display: "flex",
                }}
              >
                <div
                  style={{
                    alignSelf: "stretch",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    gap: 8,
                    display: "flex",
                  }}
                >
                  <div
                    style={{
                      alignSelf: "stretch",
                      display: "flex",
                      gap: 12,
                    }}
                  >
                    <Button
                      onClick={handlePasswordModalCancel}
                      style={{
                        flex: 1,
                        paddingLeft: 16,
                        paddingRight: 16,
                        paddingTop: 12,
                        paddingBottom: 12,
                        background: "#FFFFFF",
                        border: "1px solid #E0E5EB",
                        borderRadius: 6,
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 8,
                        display: "flex",
                        color: "#636972",
                        fontSize: 12,
                        fontFamily: "Inter",
                        fontWeight: "600",
                        lineHeight: "18px",
                        cursor: "pointer",
                        height: "auto",
                      }}
                    >
                      Cancel
                    </Button>

                    <Button
                      loading={twoFALoading}
                      htmlType="submit"
                      disabled={!validateCurrentPasswordInput(password)}
                      style={{
                        flex: 1,
                        paddingLeft: 16,
                        paddingRight: 16,
                        paddingTop: 12,
                        paddingBottom: 12,
                        background: validateCurrentPasswordInput(password)
                          ? "var(--Main-blue, #143E9F)"
                          : "#CBD0D6",
                        boxShadow: "0px 8px 24px -8px rgba(20, 62, 159, 0.16)",
                        overflow: "hidden",
                        borderRadius: 6,
                        border: "none",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 8,
                        display: "flex",
                        color: "white",
                        fontSize: 12,
                        fontFamily: "Inter",
                        fontWeight: "600",
                        lineHeight: "18px",
                        cursor: validateCurrentPasswordInput(password)
                          ? "pointer"
                          : "not-allowed",
                        height: "auto",
                      }}
                    >
                      {pendingAction === "enable"
                        ? "Enable 2FA"
                        : "Disable 2FA"}
                    </Button>
                  </div>
                </div>
              </div>
            </Form>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ManageSessionsPage;
